import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { isFlowHttpMutationsEnabled } from '@/config/flow-mutations';
import {
  emitFlowSyncUnified,
  type FlowSyncClientPayload,
  type FlowSyncEmitResult,
} from '@/state/mutations/flows/emit-flow-sync';
import { GET_FLOW_QUERY_KEY } from '@/state/queries/flows/use-get-flow';
import { useSocketStore } from '@/state/stores/use-socket-store';

/**
 * Shared wiring for schedule flow mutations: WebSocket `flowSync` or HTTP POST …/mutations when
 * {@link isFlowHttpMutationsEnabled} is true.
 */
export function useFlowSyncTransport(flowIdOverride?: string) {
  const { flowId: flowIdParam } = useParams();
  const flowId = flowIdOverride ?? flowIdParam ?? '';
  const [searchParams] = useSearchParams();
  const eventSlotIdParam = searchParams.get('eventSlotId');
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const socket = useSocketStore((s) => s.socket);
  const isConnected = Boolean(socket?.connected);

  const queryKey = useMemo(
    () => [GET_FLOW_QUERY_KEY, flowId || undefined, eventSlotIdParam ?? undefined],
    [flowId, eventSlotIdParam],
  );

  const useHttp = isFlowHttpMutationsEnabled();
  /** Transport is available and {@link flowId} is set (emit bails out without an id). */
  const canEmit = Boolean(flowId) && (useHttp || isConnected);

  const emitFlowSync = useCallback(
    async (payload: FlowSyncClientPayload, options?: { idempotencyKey?: string }): Promise<FlowSyncEmitResult> => {
      if (!flowId) return { ok: false };
      const token = await getToken();
      if (!token) return { ok: false };
      return emitFlowSyncUnified({
        socket,
        isConnected,
        payload,
        flowId,
        token,
        queryClient,
        queryKey,
        idempotencyKey: options?.idempotencyKey,
      });
    },
    [flowId, socket, isConnected, getToken, queryClient, queryKey],
  );

  return { emitFlowSync, canEmit, useHttp, isConnected };
}
